import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { ApprovalStatusBadge } from '@/components/efs/ApprovalStatusBadge';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { MaterialsFormDialog } from './MaterialsFormDialog';
import { MaterialsRowActions } from './MaterialsRowActions';
import { useMaterialsList } from './useMaterials';
import type { Material } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const MaterialsListPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);

  // Audit-issue navigation intents may carry a search/highlight query
  // (spec-13 §6.2) pointing at the affected master row.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightQ = searchParams.get('q');
  const query = useMaterialsList({ page, per_page: perPage });

  const columns = useMemo<MasterTableColumn<Material>[]>(
    () => [
      {
        key: 'japanese_name',
        header: t('efs.masters.fields.japanese_name', { ns: 'masters' }),
        cell: (row) => row.japanese_name,
      },
      {
        key: 'normalized_name',
        header: t('efs.masters.fields.normalized_name', { ns: 'masters' }),
        cell: (row) => <span className="font-mono text-xs">{row.normalized_name}</span>,
      },
      {
        key: 'material_type',
        header: t('efs.masters.fields.material_type', { ns: 'masters' }),
        cell: (row) =>
          t(`efs.masters.material_type.${row.material_type}`, {
            ns: 'masters',
            defaultValue: row.material_type,
          }),
      },
      {
        key: 'default_destination_type',
        header: t('efs.masters.fields.default_destination_type', { ns: 'masters' }),
        cell: (row) =>
          t(`efs.masters.destination_type.${row.default_destination_type}`, {
            ns: 'masters',
            defaultValue: row.default_destination_type,
          }),
      },
      {
        key: 'flags',
        header: t('efs.masters.fields.flags', { ns: 'masters' }),
        cell: (row) => {
          const flags = [
            row.price_reference_flag
              ? t('efs.masters.fields.price_reference_flag_short', {
                  ns: 'masters',
                  defaultValue: 'PR',
                })
              : null,
            row.hazardous_or_controlled_flag
              ? t('efs.masters.fields.hazardous_or_controlled_flag_short', {
                  ns: 'masters',
                  defaultValue: 'HZ',
                })
              : null,
            row.is_high_value
              ? t('efs.masters.fields.is_high_value_short', {
                  ns: 'masters',
                  defaultValue: '高価',
                })
              : null,
          ].filter(Boolean);
          return <span className="text-xs text-efs-text-muted">{flags.join(' / ')}</span>;
        },
      },
      {
        key: 'approval_status',
        header: t('efs.masters.fields.approval_status', { ns: 'masters' }),
        cell: (row) => <ApprovalStatusBadge status={row.approval_status} />,
      },
      {
        key: 'active_flag',
        header: t('efs.masters.fields.active', { ns: 'masters' }),
        cell: (row) => (
          <Badge variant={row.active_flag ? 'default' : 'secondary'}>
            {t(row.active_flag ? 'efs.masters.status.active' : 'efs.masters.status.inactive', {
              ns: 'masters',
            })}
          </Badge>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <MaterialsRowActions row={row} />,
      },
    ],
    [t],
  );

  const rows = query.data?.rows ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.material.title', { ns: 'masters' })}
        subtitle={t('efs.masters.material.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.masters.actions.create', { ns: 'masters' })}
          </Button>
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="materials" />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.material_id}
        rowProps={(row) =>
          (!!highlightId && row.material_id === highlightId) ||
          (!!highlightQ && row.japanese_name === highlightQ)
            ? { className: 'bg-efs-bg-subtle ring-1 ring-efs-accent' }
            : undefined
        }
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <MaterialsFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default MaterialsListPage;
